import React, { type JSX, useState } from "react";
import SVG from "react-inlinesvg";
import Modal from "react-modal";
import { t } from "ttag";

import iconCircleClose from "../../../../img/icons/circle-close.svg";
import {
    IAddress,
    IBillingAddress,
    IShippingAddress,
} from "../../../models/address";
import { focusElement } from "../../../utils/keyboardFocus";
import { IReduxFormState } from "../reducers.interfaces";

import styles from "./AddressSuggestionModal.module.scss";

const modalStyle = {
    content: {
        top: "5%",
        bottom: "auto",
        left: "50%",
        right: "auto",
        width: "400px",
        padding: "30px",
        marginLeft: "-200px",
        overflow: "auto",
    },
};

const extractFormAddress = (
    form: IReduxFormState,
    addressPrefix: string,
): Partial<IAddress> => {
    return [
        "first_name",
        "last_name",
        "line1",
        "line2",
        "line4",
        "state",
        "postcode",
    ].reduce((address, field) => {
        const key = (addressPrefix + "_" + field) as keyof (
            | IShippingAddress
            | IBillingAddress
        );
        return { ...address, [field]: form[key] };
    }, {});
};

const handleModalAfterOpen = (): void => {
    focusElement(`.${styles.closeButton}`);
};

export const AddressSuggestionModal = (props: {
    form: IReduxFormState;
    suggestedAddress: IAddress | null;
    onContinue: (shouldImport: boolean) => void;
    onRequestClose: () => void;
    onAfterClose: () => void;
    showAddressSuggestionModal: boolean;
    addressPrefix: string;
}): React.ReactNode => {
    const [acceptAddressSuggestion, setAcceptAddressSuggestion] =
        useState<boolean>(true);

    if (!props.suggestedAddress) {
        return null;
    }

    const formAddress = extractFormAddress(props.form, props.addressPrefix);

    const handleContinue = (): void => {
        props.onContinue(acceptAddressSuggestion);
    };

    const handleClose = (): void => {
        props.onRequestClose();
    };

    let suggestedLine2: JSX.Element | null = null;
    if (props.suggestedAddress && props.suggestedAddress.line2) {
        suggestedLine2 = (
            <span className={styles.addressLine}>
                {props.suggestedAddress.line2}
            </span>
        );
    }

    let givenLine2: JSX.Element | null = null;
    if (formAddress.line2) {
        givenLine2 = (
            <span className={styles.addressLine}>{formAddress.line2}</span>
        );
    }

    return (
        <Modal
            style={modalStyle}
            isOpen={props.showAddressSuggestionModal}
            onRequestClose={handleClose}
            onAfterClose={props.onAfterClose}
            role="dialog"
            aria={{
                labelledby: "address-verification",
                modal: true,
            }}
            onAfterOpen={handleModalAfterOpen}
        >
            <button
                className={styles.closeButton}
                aria-label={t`close`}
                onClick={handleClose}
            >
                <SVG
                    aria-hidden="true"
                    className={styles.closeIcon}
                    src={iconCircleClose}
                    title={t`Close Icon`}
                />
            </button>
            <div className={styles.root}>
                <h1 className="ada-screenreader-only" id="address-verification">
                    {t`Address Verification`}
                </h1>
                <h2 className={styles.header}>
                    {t`Please verify your address`}
                </h2>
                <fieldset className={styles.form}>
                    <legend>
                        <p>{t`USPS recommends using this address.`}</p>
                    </legend>
                    <label htmlFor="use_suggested_address">
                        <input
                            type="radio"
                            name="address_group"
                            id="use_suggested_address"
                            className={styles.radioInput}
                            checked={acceptAddressSuggestion}
                            onChange={() => setAcceptAddressSuggestion(true)}
                        />{" "}
                        <span className={styles.radioTitle}>
                            {t`Use the address suggested by USPS:`}
                        </span>
                        <p className="address-suggestion-model__address">
                            {/* TODO(i18n): Keep name format together with interpolation: {t`${first_name} ${last_name}`} - don't assume space separation or name order */}
                            <span className={styles.addressLine}>
                                {props.suggestedAddress.first_name}{" "}
                                {props.suggestedAddress.last_name}
                            </span>
                            <span className={styles.addressLine}>
                                {props.suggestedAddress.line1}
                            </span>
                            {suggestedLine2}
                            {/* TODO(i18n): Keep complete address format together with interpolation: {t`${city}, ${state} ${postcode}`} - don't assume comma/space positions or city-state-zip order */}
                            <span className={styles.addressLine}>
                                {props.suggestedAddress.line4},{" "}
                                {props.suggestedAddress.state}{" "}
                                {props.suggestedAddress.postcode}
                            </span>
                        </p>
                    </label>
                    <label htmlFor="use_input_address">
                        <input
                            type="radio"
                            name="address_group"
                            id="use_input_address"
                            className={styles.radioInput}
                            checked={!acceptAddressSuggestion}
                            onChange={() => setAcceptAddressSuggestion(false)}
                        />{" "}
                        <span className={styles.radioTitle}>
                            {t`Use the address you entered:`}
                        </span>
                        <p className="address-suggestion-model__address">
                            {/* TODO(i18n): Keep name format together with interpolation: {t`${first_name} ${last_name}`} - don't assume space separation or name order */}
                            <span className={styles.addressLine}>
                                {formAddress.first_name} {formAddress.last_name}
                            </span>
                            <span className={styles.addressLine}>
                                {formAddress.line1}
                            </span>
                            {givenLine2}
                            {/* TODO(i18n): Keep complete address format together with interpolation: {t`${city}, ${state} ${postcode}`} - don't assume comma/space positions or city-state-zip order */}
                            <span className={styles.addressLine}>
                                {formAddress.line4}, {formAddress.state}{" "}
                                {formAddress.postcode}
                            </span>
                        </p>
                    </label>
                </fieldset>
                <button
                    className={`${styles.continueButton} button`}
                    onClick={handleContinue}
                >
                    <span>{t`CONTINUE`}</span>
                </button>
            </div>
        </Modal>
    );
};
